import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type WorkspaceId =
  | 'pulse'
  | 'diamondedge'
  | 'courtedge'
  | 'griironedge'
  | 'iceedge'
  | 'pitchedge'
  | 'octagonedge'
  | 'portfolio'
  | 'observatory'
  | 'settings';

export type WorkspaceDensity = 'tactical' | 'analytical' | 'monitoring';

export type PulseStreamTab = 'agents' | 'signals' | 'alerts';

interface OSState {
  // ── Active workspace ──────────────────────────────────────────────────────
  activeWorkspace: WorkspaceId;
  setActiveWorkspace: (id: WorkspaceId) => void;

  // ── Intelligence Rail (left collapsible panel, 240px) ─────────────────────
  intelligenceRailOpen: boolean;
  toggleIntelligenceRail: () => void;
  setIntelligenceRailOpen: (open: boolean) => void;

  // ── Pulse Stream (right collapsible panel, 280px) ─────────────────────────
  pulseStreamOpen: boolean;
  pulseStreamTab: PulseStreamTab;
  togglePulseStream: () => void;
  setPulseStreamOpen: (open: boolean) => void;
  setPulseStreamTab: (tab: PulseStreamTab) => void;

  // ── Command Bar (⌘K) ──────────────────────────────────────────────────────
  commandBarOpen: boolean;
  openCommandBar: () => void;
  closeCommandBar: () => void;

  // ── Workspace density ─────────────────────────────────────────────────────
  density: WorkspaceDensity;
  setDensity: (density: WorkspaceDensity) => void;

  // ── Focus mode (dims rails to 20% opacity) ────────────────────────────────
  focusMode: boolean;
  toggleFocusMode: () => void;

  // ── Active game canvas (inline game intelligence panel) ───────────────────
  activeGameId: string | null;
  activeGameSport: string | null;
  openGameCanvas: (gameId: string, sport: string) => void;
  closeGameCanvas: () => void;
}

export const useOSStore = create<OSState>()(
  persist<OSState>(
    (set) => ({
      // ── Active workspace ────────────────────────────────────────────────────
      activeWorkspace: 'pulse',
      setActiveWorkspace: (id: WorkspaceId) => set({ activeWorkspace: id }),

      // ── Intelligence Rail ───────────────────────────────────────────────────
      intelligenceRailOpen: false,
      toggleIntelligenceRail: () => set((s: OSState) => ({ intelligenceRailOpen: !s.intelligenceRailOpen })),
      setIntelligenceRailOpen: (open: boolean) => set({ intelligenceRailOpen: open }),

      // ── Pulse Stream ────────────────────────────────────────────────────────
      pulseStreamOpen: true,
      pulseStreamTab: 'agents',
      togglePulseStream: () => set((s: OSState) => ({ pulseStreamOpen: !s.pulseStreamOpen })),
      setPulseStreamOpen: (open: boolean) => set({ pulseStreamOpen: open }),
      setPulseStreamTab: (tab: PulseStreamTab) => set({ pulseStreamTab: tab }),

      // ── Command Bar ─────────────────────────────────────────────────────────
      commandBarOpen: false,
      openCommandBar: () => set({ commandBarOpen: true }),
      closeCommandBar: () => set({ commandBarOpen: false }),

      // ── Density ─────────────────────────────────────────────────────────────
      density: 'tactical',
      setDensity: (density: WorkspaceDensity) => set({ density }),

      // ── Focus Mode ──────────────────────────────────────────────────────────
      focusMode: false,
      toggleFocusMode: () => set((s: OSState) => ({ focusMode: !s.focusMode })),

      // ── Active game canvas ──────────────────────────────────────────────────
      activeGameId: null,
      activeGameSport: null,
      openGameCanvas: (gameId: string, sport: string) => set({ activeGameId: gameId, activeGameSport: sport }),
      closeGameCanvas: () => set({ activeGameId: null, activeGameSport: null }),
    }),
    {
      name: 'navyrx-os-state',
      storage: createJSONStorage(() => localStorage),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      partialize: (state: OSState) => ({
        intelligenceRailOpen: state.intelligenceRailOpen,
        pulseStreamOpen: state.pulseStreamOpen,
        pulseStreamTab: state.pulseStreamTab,
        density: state.density,
      }) as unknown as OSState,
    },
  ),
);
